(function attachTailiHouseJourney(global) {
  'use strict';

  function createWheelGestureGate(quietWindowMs) {
    const quietMs = Number(quietWindowMs) || 180;
    let locked = false;
    let lastEventTime = -Infinity;

    function normalizeTime(timestamp) {
      return Number.isFinite(timestamp) ? timestamp : Date.now();
    }

    function accept(timestamp) {
      const currentTime = normalizeTime(timestamp);
      const quietLongEnough = currentTime - lastEventTime >= quietMs;
      lastEventTime = currentTime;

      if (!locked) return true;
      if (!quietLongEnough) return false;

      locked = false;
      return true;
    }

    function lock(timestamp) {
      locked = true;
      lastEventTime = normalizeTime(timestamp);
    }

    function block(timestamp) {
      lock(timestamp);
      return false;
    }

    return { accept, block, lock };
  }

  function isJourneyViewportEngaged(rect, viewportHeight, tolerance) {
    if (!rect || !Number.isFinite(viewportHeight)) return false;
    const edgeTolerance = Number.isFinite(tolerance) ? tolerance : 3;
    return rect.top <= edgeTolerance && rect.bottom >= viewportHeight - edgeTolerance;
  }

  function canInitializeJourneyVideo(video) {
    return Boolean(video && video.duration > 0 && video.readyState >= 2);
  }

  function shouldShowJourneyHotspots(options) {
    const { index, targetIndex, motionMode, opacity } = options || {};
    const targetIsVisuallySettled = index === targetIndex && opacity >= 0.82;
    return opacity >= 0.82 && (motionMode === 'idle' || targetIsVisuallySettled);
  }

  async function primeVideoBeforeReveal(options) {
    const { seek, video, targetTime, isCurrent, reveal } = options || {};
    if (typeof seek !== 'function' || !video || typeof reveal !== 'function') return false;
    const frameReady = await seek(video, targetTime);
    if (!frameReady || (typeof isCurrent === 'function' && !isCurrent())) return false;
    reveal();
    return true;
  }

  function scrollPastJourney(root, direction, reducedMotion) {
    if (!root) return false;
    const container = root.closest?.('.shopify-section') || root;
    const target = direction > 0
      ? container.nextElementSibling
      : container.previousElementSibling;

    if (!target || typeof target.scrollIntoView !== 'function') return false;
    target.scrollIntoView({
      behavior: reducedMotion ? 'auto' : 'smooth',
      block: 'start'
    });
    return true;
  }

  function recoverJourneyTransition(options) {
    const {
      video,
      duration,
      nodeTimes,
      index,
      stop,
      settle
    } = options || {};
    const targetTime = nodeTimes?.[index];

    if (!video || !Number.isFinite(targetTime) || typeof settle !== 'function') return false;
    if (typeof stop === 'function') stop();
    video.currentTime = Math.min(targetTime, Math.max(0, duration - 0.001));
    settle(index);
    return true;
  }

  function initMobileJourney(root, viewportQuery) {
    const mobileTrack = root.querySelector('[data-journey-mobile]');
    if (!mobileTrack) return;

    root.dataset.ready = 'true';
    root.dataset.mobileMode = 'true';

    function goToScene(index, behavior) {
      if (!Number.isFinite(index)) return;
      mobileTrack.scrollTo({
        left: index * mobileTrack.clientWidth,
        behavior: behavior || 'smooth'
      });
    }

    function handleThemeBlockSelect(event) {
      if (!root.contains(event.target)) return;
      const mobileScene = event.target.closest('[data-mobile-scene-index]');
      const desktopHotspot = event.target.closest('[data-journey-hotspot]');

      if (mobileScene) {
        goToScene(Number(mobileScene.dataset.mobileSceneIndex));
      } else if (desktopHotspot) {
        goToScene(Number(desktopHotspot.dataset.sceneIndex));
      }
    }

    function handleViewportChange(event) {
      if (event.matches) return;
      root._tailiHouseJourneyCleanup();
      initJourney(root);
    }

    document.addEventListener('shopify:block:select', handleThemeBlockSelect);
    viewportQuery.addEventListener('change', handleViewportChange);

    root._tailiHouseJourneyCleanup = () => {
      document.removeEventListener('shopify:block:select', handleThemeBlockSelect);
      viewportQuery.removeEventListener('change', handleViewportChange);
      delete root.dataset.mobileMode;
      root.dataset.journeyInitialized = 'false';
    };
  }

  function initJourney(root) {
    if (!root || root.dataset.journeyInitialized === 'true') return;
    const viewportQuery = global.matchMedia('(max-width: 767px)');

    if (viewportQuery.matches) {
      root.dataset.journeyInitialized = 'true';
      initMobileJourney(root, viewportQuery);
      return;
    }

    if (!global.TailiVideoLoader || !global.TailiVideoSeek) return;

    root.dataset.journeyInitialized = 'true';

    const forwardVideo = root.querySelector('[data-journey-forward]');
    const reverseVideo = root.querySelector('[data-journey-reverse]');
    const eyebrow = root.querySelector('[data-journey-eyebrow]');
    const title = root.querySelector('[data-journey-title]');
    const body = root.querySelector('[data-journey-body]');
    const cta = root.querySelector('[data-journey-cta]');
    const hint = root.querySelector('[data-journey-hint]');
    const count = root.querySelector('[data-journey-count]');
    const sceneElements = [...root.querySelectorAll('[data-journey-scene]')];
    const hotspots = [...root.querySelectorAll('[data-journey-hotspot]')];
    const hotspotStates = hotspots.map((hotspot) => ({
      element: hotspot,
      wrapper: hotspot.querySelector('[data-lookbook-hotspot-wrapper]'),
      sceneIndex: Number(hotspot.dataset.sceneIndex),
      visible: false
    }));
    const scenes = sceneElements.map((scene) => ({
      eyebrow: scene.dataset.eyebrow || '',
      title: scene.dataset.title || '',
      body: scene.dataset.body || '',
      cta: scene.dataset.cta || '',
      link: scene.dataset.link || '#',
      position: scene.dataset.position || 'left'
    }));

    if (!forwardVideo || !reverseVideo || !scenes.length) return;

    const mediaQuery = global.matchMedia('(prefers-reduced-motion: reduce)');
    const seekCoordinator = global.TailiVideoSeek.createCoordinator();
    const forwardThreshold = Number(root.dataset.wheelThreshold) || 520;
    const wheelGestureGate = createWheelGestureGate(180);
    const sourceNodeTimes = [0, 5 + 11 / 24, 10 + 11 / 24, 15 + 11 / 24, 20 + 11 / 24];
    const sourceJourneyEnd = sourceNodeTimes[sourceNodeTimes.length - 1];
    const cleanups = [];

    let duration = 0;
    let reverseDuration = 0;
    let nodeTimes = [];
    let activeVideo = forwardVideo;
    let activeNode = 0;
    let targetNode = 0;
    let wheelDebt = 0;
    let ready = false;
    let reverseReady = false;
    let motionMode = 'idle';
    let motionToken = 0;
    let monitoredIndex = 0;
    let monitoredToken = 0;
    let renderedSceneIndex = -1;
    let renderedOpacity = '';
    let renderedProgress = '';
    let renderedHint = '';

    function clamp(value, min, max) {
      return Math.min(Math.max(value, min), max);
    }

    function canonicalToReverse(time) {
      return global.TailiVideoSeek.mapCanonicalToReverse(time, duration, reverseDuration);
    }

    function reverseToCanonical(time) {
      if (!duration || !reverseDuration) return 0;
      return clamp(duration - (time / reverseDuration) * duration, 0, duration);
    }

    function currentTime() {
      return activeVideo === reverseVideo
        ? reverseToCanonical(reverseVideo.currentTime)
        : forwardVideo.currentTime;
    }

    function nearestNodeIndex(time) {
      let nearest = 0;
      let distance = Infinity;
      nodeTimes.forEach((nodeTime, index) => {
        const nextDistance = Math.abs(nodeTime - time);
        if (nextDistance < distance) {
          nearest = index;
          distance = nextDistance;
        }
      });
      return nearest;
    }

    function renderScene(index, opacity) {
      const scene = scenes[index] || scenes[0];
      const nextOpacity = opacity.toFixed(3);

      if (nextOpacity !== renderedOpacity) {
        root.style.setProperty('--taili-journey-copy-opacity', nextOpacity);
        renderedOpacity = nextOpacity;
      }

      if (index === renderedSceneIndex) return;

      root.dataset.scene = String(index);
      root.dataset.copyPosition = scene.position;
      eyebrow.textContent = scene.eyebrow;
      title.textContent = scene.title;
      body.textContent = scene.body;
      cta.textContent = scene.cta;
      cta.href = scene.link || '#';
      cta.hidden = !scene.cta;
      count.textContent = `${String(index + 1).padStart(2, '0')} / ${String(scenes.length).padStart(2, '0')}`;
      renderedSceneIndex = index;
    }

    function renderHotspots(index, opacity) {
      const sceneIsSettled = shouldShowJourneyHotspots({
        index,
        targetIndex: targetNode,
        motionMode,
        opacity
      });

      hotspotStates.forEach((state) => {
        const belongsToScene = state.sceneIndex === index;
        const isVisible = sceneIsSettled && belongsToScene;

        if (state.visible === isVisible) return;

        state.element.classList.toggle('is-visible', isVisible);
        state.element.setAttribute('aria-hidden', String(!isVisible));
        state.visible = isVisible;

        if (!isVisible && state.wrapper) {
          state.wrapper.classList.remove('active');
        }
      });
    }

    function setHint(message) {
      if (message === renderedHint) return;
      hint.textContent = message;
      renderedHint = message;
    }

    function updateUi() {
      if (!duration || !nodeTimes.length) return;
      const time = currentTime();
      const nearest = nearestNodeIndex(time);
      const distance = Math.abs(time - nodeTimes[nearest]);
      const fadeWindow = Math.max(0.42, duration * 0.045);
      const opacity = clamp(1 - distance / fadeWindow, 0, 1);
      const renderedOpacity = opacity < 0.08 ? 0 : opacity;

      renderScene(nearest, renderedOpacity);
      renderHotspots(nearest, renderedOpacity);
      const nextProgress = clamp(time / duration, 0, 1).toFixed(4);
      if (nextProgress !== renderedProgress) {
        root.style.setProperty('--taili-journey-progress', nextProgress);
        renderedProgress = nextProgress;
      }

      if (motionMode !== 'idle') {
        setHint(motionMode === 'reverse' ? 'Moving to the previous room' : 'Moving to the next room');
      } else if (activeNode === scenes.length - 1) {
        setHint('Scroll down to continue');
      } else {
        setHint('Scroll to move through the house');
      }
    }

    function showForward() {
      activeVideo = forwardVideo;
      forwardVideo.classList.remove('is-hidden');
      reverseVideo.classList.add('is-hidden');
    }

    function showReverse() {
      activeVideo = reverseVideo;
      reverseVideo.classList.remove('is-hidden');
      forwardVideo.classList.add('is-hidden');
    }

    const progressWatchdog = global.TailiVideoSeek.createProgressWatchdog(() => {
      recoverAtNode(targetNode, motionToken);
    }, 2500);

    function stopVideos() {
      progressWatchdog.stop();
      forwardVideo.pause();
      reverseVideo.pause();
      forwardFrameLoop.stop();
      reverseFrameLoop.stop();
    }

    function cancelMotion() {
      motionToken += 1;
      motionMode = 'idle';
      wheelDebt = 0;
      stopVideos();
      if (ready && activeVideo === forwardVideo && Number.isFinite(nodeTimes[activeNode])) {
        forwardVideo.currentTime = Math.min(nodeTimes[activeNode], Math.max(0, duration - 0.001));
        showForward();
      }
      renderScene(activeNode, 1);
      updateUi();
    }

    function settleAtNode(index, revealForwardVideo) {
      if (revealForwardVideo !== false) showForward();
      activeNode = index;
      targetNode = index;
      motionMode = 'idle';
      wheelDebt = 0;
      renderScene(index, 1);
      updateUi();
    }

    function recoverAtNode(index, token) {
      if (token !== motionToken || !Number.isFinite(nodeTimes[index])) return;
      recoverJourneyTransition({
        video: forwardVideo,
        duration,
        nodeTimes,
        index,
        stop: stopVideos,
        settle: settleAtNode
      });
    }

    function finishAtNode(index, token) {
      if (token !== motionToken) return;
      if (motionMode === 'reverse') {
        stopVideos();
        settleAtNode(index, false);
        primeVideoBeforeReveal({
          seek: (video, time) => seekCoordinator.seek(video, time),
          video: forwardVideo,
          targetTime: nodeTimes[index],
          isCurrent: () => token === motionToken && activeNode === index && motionMode === 'idle',
          reveal: showForward
        });
        return;
      }
      recoverAtNode(index, token);
    }

    function monitorForward() {
      const index = monitoredIndex;
      const token = monitoredToken;
      if (token !== motionToken || motionMode !== 'forward') return;
      progressWatchdog.ping();
      updateUi();
      if (forwardVideo.currentTime >= nodeTimes[index] - 0.025) {
        finishAtNode(index, token);
      }
    }

    function monitorReverse() {
      const index = monitoredIndex;
      const token = monitoredToken;
      if (token !== motionToken || motionMode !== 'reverse') return;
      progressWatchdog.ping();
      updateUi();
      if (currentTime() <= nodeTimes[index] + 0.035) {
        finishAtNode(index, token);
      }
    }

    const forwardFrameLoop = global.TailiVideoSeek.createFrameLoop(forwardVideo, monitorForward);
    const reverseFrameLoop = global.TailiVideoSeek.createFrameLoop(reverseVideo, monitorReverse);

    async function playForwardTo(index) {
      if (!ready || motionMode !== 'idle' || index <= activeNode) return;
      const token = ++motionToken;
      const startTime = currentTime();
      targetNode = clamp(index, 0, scenes.length - 1);
      motionMode = 'preparing';
      wheelDebt = 0;
      stopVideos();

      if (
        activeVideo !== forwardVideo ||
        forwardVideo.readyState < 2 ||
        Math.abs(forwardVideo.currentTime - startTime) > 0.05
      ) {
        const frameReady = await seekCoordinator.seek(forwardVideo, startTime);
        if (!frameReady || token !== motionToken) {
          if (token === motionToken) {
            motionMode = 'idle';
            updateUi();
          }
          return;
        }
      }

      if (token !== motionToken) return;
      showForward();
      motionMode = 'forward';
      forwardVideo.playbackRate = 1;
      forwardVideo.play().catch(() => cancelMotion());
      monitoredIndex = targetNode;
      monitoredToken = token;
      progressWatchdog.start();
      forwardFrameLoop.start();
    }

    async function playReverseTo(index) {
      if (!ready || motionMode !== 'idle' || index >= activeNode) return;
      const token = ++motionToken;
      const startTime = currentTime();
      targetNode = clamp(index, 0, scenes.length - 1);
      wheelDebt = 0;
      stopVideos();

      if (!reverseReady || reverseVideo.readyState < 2) {
        motionMode = 'preparing';
        recoverAtNode(targetNode, token);
        return;
      }

      motionMode = 'preparing';
      const seekReady = await seekCoordinator.seek(reverseVideo, canonicalToReverse(startTime));
      if (!seekReady || token !== motionToken) {
        cancelMotion();
        return;
      }

      showReverse();
      motionMode = 'reverse';
      reverseVideo.playbackRate = 1;
      reverseVideo.play().catch(() => cancelMotion());
      monitoredIndex = targetNode;
      monitoredToken = token;
      progressWatchdog.start();
      reverseFrameLoop.start();
    }

    function sectionIsEngaged() {
      return isJourneyViewportEngaged(root.getBoundingClientRect(), global.innerHeight);
    }

    function handleWheel(event) {
      if (!ready || mediaQuery.matches || !sectionIsEngaged()) return;
      const eventTime = Number.isFinite(event.timeStamp)
        ? event.timeStamp
        : global.performance?.now?.() || Date.now();

      if (motionMode !== 'idle') {
        wheelGestureGate.block(eventTime);
        event.preventDefault();
        return;
      }

      if (!wheelGestureGate.accept(eventTime)) {
        event.preventDefault();
        return;
      }

      if (event.deltaY > 0 && activeNode === scenes.length - 1) {
        wheelGestureGate.lock(eventTime);
        event.preventDefault();
        scrollPastJourney(root, 1, mediaQuery.matches);
        return;
      }
      if (event.deltaY < 0 && activeNode === 0) return;

      event.preventDefault();
      if (Math.abs(event.deltaY) < 18) return;

      if (event.deltaY < 0) {
        wheelDebt = 0;
        wheelGestureGate.lock(eventTime);
        playReverseTo(activeNode - 1);
        return;
      }

      wheelDebt += event.deltaY;
      const remaining = Math.max(0, forwardThreshold - wheelDebt);
      setHint(remaining > 0
        ? `Scroll ${Math.ceil(remaining)} more`
        : 'Moving to the next room');

      if (wheelDebt >= forwardThreshold) {
        wheelGestureGate.lock(eventTime);
        playForwardTo(activeNode + 1);
      }
    }

    function handleKeydown(event) {
      if (!ready || !sectionIsEngaged()) return;

      if ((event.key === 'ArrowRight' || event.key === ' ') && activeNode < scenes.length - 1) {
        event.preventDefault();
        playForwardTo(activeNode + 1);
      }

      if (event.key === 'ArrowLeft' && activeNode > 0) {
        event.preventDefault();
        playReverseTo(activeNode - 1);
      }
    }

    function handleForwardMetadata() {
      if (ready) return;
      if (!canInitializeJourneyVideo(forwardVideo)) return;
      duration = Math.max(0, forwardVideo.duration || 0);

      nodeTimes = sourceNodeTimes.slice(0, scenes.length).map((time) => (
        Number(((time / sourceJourneyEnd) * duration).toFixed(4))
      ));

      seekCoordinator.seek(forwardVideo, nodeTimes[0]).then((seekReady) => {
        if (!seekReady) return;
        ready = true;
        activeNode = 0;
        targetNode = 0;
        root.dataset.ready = 'true';
        showForward();
        renderScene(0, 1);
        updateUi();
      });
    }

    function handleReverseMetadata() {
      reverseDuration = Math.max(0, reverseVideo.duration || 0);
      reverseReady = reverseDuration > 0;
    }

    function handleVisibilityChange() {
      if (document.hidden && motionMode !== 'idle') cancelMotion();
    }

    function handleViewportChange(event) {
      if (!event.matches) return;
      root._tailiHouseJourneyCleanup();
      initJourney(root);
    }

    function handleHotspotEnter(event) {
      const selected = event.currentTarget.querySelector('[data-lookbook-hotspot-wrapper]');
      hotspots.forEach((hotspot) => {
        const wrapper = hotspot.querySelector('[data-lookbook-hotspot-wrapper]');
        if (wrapper) wrapper.classList.toggle('active', wrapper === selected);
      });
    }

    function handleHotspotLeave(event) {
      const wrapper = event.currentTarget.querySelector('[data-lookbook-hotspot-wrapper]');
      if (wrapper) wrapper.classList.remove('active');
    }

    async function showSceneImmediately(index) {
      if (!ready || !Number.isFinite(nodeTimes[index])) return;
      cancelMotion();
      const token = motionToken;
      const seekReady = await seekCoordinator.seek(forwardVideo, nodeTimes[index]);
      if (!seekReady || token !== motionToken) return;
      showForward();
      activeNode = index;
      targetNode = index;
      motionMode = 'idle';
      updateUi();
    }

    function handleThemeBlockSelect(event) {
      if (!root.contains(event.target)) return;
      const hotspot = event.target.closest('[data-journey-hotspot]');
      if (!hotspot) return;
      showSceneImmediately(Number(hotspot.dataset.sceneIndex));
    }

    forwardVideo.addEventListener('loadedmetadata', handleForwardMetadata);
    forwardVideo.addEventListener('loadeddata', handleForwardMetadata);
    reverseVideo.addEventListener('loadedmetadata', handleReverseMetadata);
    global.addEventListener('wheel', handleWheel, { passive: false });
    global.addEventListener('keydown', handleKeydown);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('shopify:block:select', handleThemeBlockSelect);
    viewportQuery.addEventListener('change', handleViewportChange);

    hotspots.forEach((hotspot) => {
      hotspot.addEventListener('mouseenter', handleHotspotEnter);
      hotspot.addEventListener('mouseleave', handleHotspotLeave);
    });

    cleanups.push(() => forwardVideo.removeEventListener('loadedmetadata', handleForwardMetadata));
    cleanups.push(() => forwardVideo.removeEventListener('loadeddata', handleForwardMetadata));
    cleanups.push(() => reverseVideo.removeEventListener('loadedmetadata', handleReverseMetadata));
    cleanups.push(() => global.removeEventListener('wheel', handleWheel));
    cleanups.push(() => global.removeEventListener('keydown', handleKeydown));
    cleanups.push(() => document.removeEventListener('visibilitychange', handleVisibilityChange));
    cleanups.push(() => document.removeEventListener('shopify:block:select', handleThemeBlockSelect));
    cleanups.push(() => viewportQuery.removeEventListener('change', handleViewportChange));
    cleanups.push(() => hotspots.forEach((hotspot) => {
      hotspot.removeEventListener('mouseenter', handleHotspotEnter);
      hotspot.removeEventListener('mouseleave', handleHotspotLeave);
    }));

    if (forwardVideo.readyState >= 1) handleForwardMetadata();
    if (reverseVideo.readyState >= 1) handleReverseMetadata();

    global.TailiVideoLoader.loadPrimaryThenDeferSecondary({
      primary: forwardVideo,
      primarySource: root.dataset.forwardSrc,
      secondary: reverseVideo,
      secondarySource: root.dataset.reverseSrc,
      onSecondaryError() {
        reverseReady = false;
      }
    }).catch(() => {
      root.dataset.ready = 'error';
      setHint('Journey video could not be loaded');
    });

    root._tailiHouseJourneyCleanup = () => {
      cancelMotion();
      cleanups.forEach((cleanup) => cleanup());
      root.dataset.journeyInitialized = 'false';
    };
  }

  function initAll(scope) {
    const target = scope || document;
    target.querySelectorAll('[data-taili-house-journey]').forEach(initJourney);
  }

  global.TailiHouseJourneyInit = initAll;

  if (typeof document === 'undefined') {
    if (typeof module !== 'undefined' && module.exports) {
      module.exports = {
        canInitializeJourneyVideo,
        createWheelGestureGate,
        isJourneyViewportEngaged,
        primeVideoBeforeReveal,
        recoverJourneyTransition,
        shouldShowJourneyHotspots,
        scrollPastJourney
      };
    }
    return;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector('[data-taili-house-journey]');
    if (root && root._tailiHouseJourneyCleanup) root._tailiHouseJourneyCleanup();
  });
})(typeof window !== 'undefined' ? window : globalThis);
